import React, { useState, useEffect, useCallback } from 'react';
import { printTicket } from '../POS/TicketPrinter';

const MenuPOS = ({
  menuItems,
  categories,
  onAddPOSSale,
  sesionActiva,
  currentOrderId,
  onOrderClosed,
  // FIX: recibir loyalty desde POSPage
  loyaltyCustomer,
  loyaltyDiscount
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [amountPaid, setAmountPaid] = useState('');
  const [changeDue, setChangeDue] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [itemVariantGroups, setItemVariantGroups] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [loadingVariants, setLoadingVariants] = useState(false);

  const [currentOrder, setCurrentOrder] = useState(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [orderName, setOrderName]         = useState('');

  const [toast, setToast] = useState(null);

  const [editingNoteIdx, setEditingNoteIdx] = useState(null);
  const [noteInput, setNoteInput]           = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (currentOrderId) {
      loadOrder(currentOrderId);
    } else {
      setCurrentOrder(null);
      setCart([]);
    }
  }, [currentOrderId]);

  const loadOrder = async (orderId) => {
    try {
      const res  = await fetch(`/api/orders/get_order_details.php?order_id=${orderId}`);
      const data = await res.json();
      if (data.success) {
        const cartItems = data.items.map(item => ({
          id:           item.product_id,
          name:         item.product_name,
          price:        Number(item.price),
          quantity:     Number(item.quantity),
          note:         item.note || '',
          variants:     item.variants || [],
          variantLabel: (item.variants || []).map(v => v.variant_name).join(', ')
        }));
        setCart(cartItems);
        setCurrentOrder(data.order);
      }
    } catch (error) {
      console.error("Error cargando orden:", error);
    }
  };

  const resetPOS = () => {
    setCart([]);
    setCurrentOrder(null);
    localStorage.removeItem('currentOrderId');
    setAmountPaid('');
    setChangeDue(0);
    setPaymentMethod('Efectivo');
    if (onOrderClosed) onOrderClosed();
  };

  const filteredItems = selectedCategory
    ? menuItems.filter(item => String(item?.category_id ?? '') === String(selectedCategory))
    : menuItems;

  const handleAddToCart = async (item) => {
    setLoadingVariants(true);
    try {
      const res    = await fetch(`/api/variants/get_menu_item_variants.php?menu_item_id=${item.id}`);
      const data   = await res.json();
      const groups = data.success ? (data.groups || []) : [];

      if (groups.length > 0) {
        setCurrentItem(item);
        setSelectedVariants({});
        setItemVariantGroups(groups);
        setModalOpen(true);
      } else {
        addItemToCart(item, [], [], '');
      }
    } catch (e) {
      console.error("Error cargando variantes:", e);
      addItemToCart(item, [], [], '');
    } finally {
      setLoadingVariants(false);
    }
  };

  const addItemToCart = (item, variantList, variantIds, note) => {
    const finalPrice   = Number(item.price) + variantList.reduce((sum, v) => sum + (Number(v.price_modifier) || 0), 0);
    const variantLabel = variantList.map(v => v.name).join(', ');

    const cartItem = {
      ...item,
      price:        finalPrice,
      quantity:     1,
      note:         note || '',
      variants:     variantList,
      variantIds:   variantIds,
      variantLabel: variantLabel
    };

    setCart(prev => {
      const key    = `${item.id}-${[...variantIds].sort().join(',')}-${note}`;
      const exists = prev.find(i =>
        `${i.id}-${[...(i.variantIds || [])].sort().join(',')}-${i.note}` === key
      );
      if (exists) {
        return prev.map(i =>
          `${i.id}-${[...(i.variantIds || [])].sort().join(',')}-${i.note}` === key
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, cartItem];
    });
  };

  const handleVariantSelect = (groupId, option) => {
    setSelectedVariants(prev => {
      if (prev[groupId]?.id === option.id) {
        const next = { ...prev };
        delete next[groupId];
        return next;
      }
      return { ...prev, [groupId]: option };
    });
  };

  const calculateModalPrice = () => {
    if (!currentItem) return 0;
    const base  = Number(currentItem.price) || 0;
    const extra = Object.values(selectedVariants).reduce((sum, opt) => sum + (Number(opt.price_modifier) || 0), 0);
    return base + extra;
  };

  const handleModalConfirm = () => {
    if (!currentItem) return;
    const variantList = Object.values(selectedVariants);
    const variantIds  = variantList.map(v => v.id);
    addItemToCart(currentItem, variantList, variantIds, '');
    setModalOpen(false);
    setCurrentItem(null);
    setSelectedVariants({});
    setItemVariantGroups([]);
  };

  const handleRemoveFromCart = (idx) => {
    setCart(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateQuantity = (idx, quantity) => {
    setCart(prev =>
      prev
        .map((item, i) => i === idx ? { ...item, quantity: Number(quantity) } : item)
        .filter(item => item.quantity > 0)
    );
  };

  const handleSaveNote = (idx) => {
    setCart(prev => prev.map((item, i) => i === idx ? { ...item, note: noteInput } : item));
    setEditingNoteIdx(null);
    setNoteInput('');
  };

  const calculateCartTotal = () =>
    cart.reduce((total, item) => total + (item.price || 0) * item.quantity, 0);

  const calculateCartTotalWithoutVAT = () =>
    cart.reduce((total, item) => total + (item.priceWithoutVAT || 0) * item.quantity, 0);

  // FIX: total visible en UI ya refleja el descuento loyalty
  const calculateFinalTotal = () =>
    Math.max(0, calculateCartTotal() - (loyaltyDiscount || 0));

  const handleAmountPaidChange = (e) => {
    const paid = Number(e.target.value) || 0;
    setAmountPaid(e.target.value);
    // FIX: cambio calculado sobre total con descuento
    setChangeDue(Math.max(0, paid - calculateFinalTotal()));
  };

  const buildItemsPayload = () =>
    cart.map(item => ({
      product_id: item.id,
      quantity:   item.quantity,
      price:      item.price,
      note:       item.note || '',
      variants:   item.variantIds || []
    }));

  const handleSaveOrder = async (name = '') => {
    if (cart.length === 0) return;
    setShowNameModal(false);

    try {
      if (currentOrder) {
        const res  = await fetch("/api/orders/update.php", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            order_id:   currentOrder.id,
            order_name: name || null,
            items:      buildItemsPayload()
          })
        });
        const data = await res.json();
        if (!data.success) { showToast("Error al actualizar orden", 'error'); return; }
        resetPOS();
        showToast("Orden actualizada");
      } else {
        const orderData = {
          total:          calculateCartTotal(),
          status:         "abierta",
          order_type:     "pos",
          user_id:        sesionActiva?.userId,
          employee_id:    sesionActiva?.employeeId,
          cash_drawer_id: sesionActiva?.cajaId,
          order_name:     name || null,
          items:          buildItemsPayload()
        };
        const res  = await fetch("/api/orders/create.php", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(orderData)
        });
        const data = await res.json();
        if (!data.success) { showToast("Error al crear orden", 'error'); return; }
        resetPOS();
        showToast("Orden guardada");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCheckout = async () => {
  if (cart.length === 0) { showToast("El carrito está vacío", 'error'); return; }

  const finalTotal = calculateFinalTotal();

  if (paymentMethod === 'Efectivo' &&
      (amountPaid === '' || Number(amountPaid) < finalTotal)) {
    showToast('Monto pagado insuficiente', 'error');
    return;
  }

  try {
    let activeOrder = currentOrder;

    if (!activeOrder) {
      const orderData = {
        total:          calculateCartTotal(),
        status:         "abierta",
        order_type:     "pos",
        user_id:        sesionActiva?.userId,
        employee_id:    sesionActiva?.employeeId,
        cash_drawer_id: sesionActiva?.cajaId,
        items:          buildItemsPayload()
      };
      const createRes  = await fetch("/api/orders/create.php", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(orderData)
      });
      const createData = await createRes.json();
      if (!createData.success) { showToast("Error al crear la orden: " + createData.error, 'error'); return; }
      activeOrder = createData.order;
    }

    const closeRes = await fetch("/api/orders/close.php", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        order_id:            activeOrder.id,
        payment_method:      paymentMethod,
        amount_paid:         amountPaid,
        total:               calculateCartTotal(),
        loyalty_customer_id: loyaltyCustomer?.id || null,
        loyalty_discount:    loyaltyDiscount      || 0
      })
    });
    const closeData = await closeRes.json();
    if (!closeData.success) { showToast("Error al cerrar orden: " + closeData.error, 'error'); return; }

    // FIX: pointsEarned declarado aquí, disponible para printTicket
    const pointsEarned = loyaltyCustomer
      ? Math.floor(Math.max(0, calculateCartTotal() - (loyaltyDiscount || 0)) / 10)
      : 0;

    let ticketSettings = {};
    try {
      const settingsRes    = await fetch("https://lezen.site/api/get_ticket_settings.php", { credentials: "include" });
      const settingsResult = await settingsRes.json();
      ticketSettings = settingsResult.success ? settingsResult.data : {};
    } catch (e) {
      console.warn("⚠️ No se pudieron cargar ticket settings:", e);
    }

    printTicket({
      order_id:         activeOrder.id,
      order_number:     closeData.order_number,
      order_name:       currentOrder?.order_name || null,
      employee_name:    closeData.employee_name,
      total:            finalTotal,
      payment_method:   paymentMethod,
      items:            cart,
      amount_paid:      Number(amountPaid) || 0,
      settings:         ticketSettings,
      // FIX: loyalty
      loyalty_customer: loyaltyCustomer || null,
      loyalty_discount: loyaltyDiscount  || 0,
      points_earned:    pointsEarned
    });

    showToast(
      paymentMethod === 'Efectivo'
        ? `Venta procesada · Cambio: $${Math.max(0, Number(amountPaid) - finalTotal).toFixed(2)}`
        : 'Venta procesada'
    );

    resetPOS();
    if (onAddPOSSale) onAddPOSSale();

  } catch (error) {
    console.error("Error en checkout:", error);
    showToast("Ocurrió un error al procesar la venta", 'error');
  }
};

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

      {/* PRODUCTOS */}
      <div className="md:col-span-2">
        <div className="bg-[#F9EFD7] p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold text-[#3F1907] mb-3">Productos</h3>

          <div className="mb-4">
            <label className="block text-[#3F1907] text-sm font-bold mb-1">
              Filtrar por Categoría
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
            >
              <option value="">Todas las Categorías</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition"
                onClick={() => handleAddToCart(item)}
              >
                {(item.image_url || item.image) && (
                  <img
                    src={item.image_url || item.image}
                    alt={item.name}
                    className="w-full h-32 object-cover"
                  />
                )}
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-[#3F1907] truncate">{item.name}</h4>
                  <p className="text-xs text-gray-600">${Number(item.price).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CARRITO */}
      <div className="md:col-span-1">
        <div className="bg-[#F9EFD7] p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-[#3F1907] mb-3">Carrito</h3>

          {cart.length === 0 ? (
            <p className="text-[#3F1907]">El carrito está vacío.</p>
          ) : (
            <ul className="divide-y divide-[#58735A]">
              {cart.map((item, idx) => (
                <li key={idx} className="py-3 text-sm text-[#3F1907]">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="font-semibold truncate">{item.name}</p>
                      {item.variantLabel && (
                        <p className="text-xs text-[#58735A]">{item.variantLabel}</p>
                      )}
                      <p className="text-xs text-gray-600">
                        ${Number(item.price).toFixed(2)} x {item.quantity}
                      </p>

                      {editingNoteIdx === idx ? (
                        <input
                          autoFocus
                          type="text"
                          value={noteInput}
                          onChange={e => setNoteInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveNote(idx); }}
                          onBlur={() => handleSaveNote(idx)}
                          placeholder="Sin azúcar, extra caliente..."
                          className="mt-1 w-full px-2 py-0.5 border border-[#58735A] rounded text-xs bg-white text-[#3F1907]"
                        />
                      ) : (
                        <button
                          onClick={() => { setEditingNoteIdx(idx); setNoteInput(item.note || ''); }}
                          className="mt-1 text-xs text-[#58735A] hover:text-[#3F1907] underline"
                        >
                          {item.note ? `Nota: ${item.note}` : '+ Agregar nota'}
                        </button>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleUpdateQuantity(idx, e.target.value)}
                        className="w-12 px-1 py-0.5 border border-[#58735A] rounded-md bg-[#F9EFD7] text-center text-[#3F1907]"
                      />
                      <button
                        onClick={() => handleRemoveFromCart(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        X
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 pt-4 border-t border-[#58735A] text-right text-[#3F1907]">
            <p className="text-sm font-medium">Subtotal: ${calculateCartTotal().toFixed(2)}</p>
            <p className="text-sm font-medium">Total (sin IVA): ${calculateCartTotalWithoutVAT().toFixed(2)}</p>

            {/* FIX: mostrar descuento y total final si hay loyalty */}
            {loyaltyDiscount > 0 && (
              <>
                <p className="text-sm text-green-700">
                  Descuento puntos: -${loyaltyDiscount.toFixed(2)}
                </p>
                <p className="text-sm font-bold text-[#3F1907]">
                  Total final: ${calculateFinalTotal().toFixed(2)}
                </p>
              </>
            )}

            <button
              onClick={() => {
                if (cart.length === 0) return;
                setOrderName(currentOrder?.order_name || '');
                setShowNameModal(true);
              }}
              disabled={cart.length === 0}
              className="mt-3 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-md"
            >
              {currentOrder ? "Actualizar Orden" : "Guardar Orden"}
            </button>

            <div className="mt-4">
              <label className="block text-left text-[#3F1907] text-sm font-bold mb-1">
                Método de Pago
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
              >
                <option value="Efectivo">Efectivo</option>
                <option value="Tarjeta">Tarjeta</option>
                <option value="Transferencia">Transferencia</option>
              </select>
            </div>

            {paymentMethod === 'Efectivo' && (
              <div className="mt-2">
                <label className="block text-left text-[#3F1907] text-sm font-bold mb-1">
                  Monto Pagado
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountPaid}
                  onChange={handleAmountPaidChange}
                  className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Cambio a Devolver: ${changeDue.toFixed(2)}
                </p>
              </div>
            )}

            <button
              onClick={handleCheckout}
              className="mt-4 w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-[#F9EFD7] font-bold py-2 px-4 rounded-md"
              disabled={
                cart.length === 0 ||
                (paymentMethod === 'Efectivo' && Number(amountPaid) < calculateFinalTotal())
              }
            >
              Cerrar y Cobrar
            </button>
          </div>
        </div>
      </div>

      {/* TOAST */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 z-[100] flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium text-white"
          style={{
            transform: 'translateX(-50%)',
            background: toast.type === 'error' ? '#A32D2D' : '#3B6D11',
            whiteSpace: 'nowrap'
          }}
        >
          {toast.type === 'error' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#F09595" strokeWidth="1.5"/>
              <path d="M6 6l4 4M10 6l-4 4" stroke="#F09595" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#97C459" strokeWidth="1.5"/>
              <path d="M5 8l2 2 4-4" stroke="#97C459" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {toast.message}
        </div>
      )}

      {/* MODAL NOMBRE DE ORDEN */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-[#F9EFD7] p-5 rounded-lg w-80">
            <h3 className="font-bold text-[#3F1907] text-lg mb-3">
              {currentOrder ? 'Actualizar orden' : 'Guardar orden'}
            </h3>
            <label className="block text-sm font-bold text-[#3F1907] mb-1">
              Nombre de mesa o referencia
              <span className="text-xs font-normal text-gray-500 ml-1">(opcional)</span>
            </label>
            <input
              type="text"
              value={orderName}
              onChange={e => setOrderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveOrder(orderName); }}
              placeholder="Ej: Mesa 5, Barra, Para llevar..."
              autoFocus
              className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-white text-[#3F1907] mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowNameModal(false)}
                className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-2 rounded-md text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleSaveOrder('')}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-md text-sm"
              >
                Omitir
              </button>
              <button
                onClick={() => handleSaveOrder(orderName)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md text-sm font-bold"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SOLO VARIANTES */}
      {modalOpen && currentItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-[#F9EFD7] p-5 rounded-lg w-96 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-[#3F1907] text-lg mb-1">{currentItem.name}</h3>
            <p className="text-sm text-[#58735A] mb-4">
              Precio base: ${Number(currentItem.price).toFixed(2)}
            </p>

            {loadingVariants ? (
              <p className="text-sm text-gray-500 mb-4">Cargando opciones...</p>
            ) : (
              itemVariantGroups.map(group => (
                <div key={group.variant_group_id} className="mb-4">
                  <p className="text-sm font-bold text-[#3F1907] mb-2">
                    {group.group_name}
                    <span className="text-xs font-normal text-gray-500 ml-1">
                      {group.is_required ? '(requerido)' : '(opcional)'}
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {group.options.map(option => {
                      const isSelected = selectedVariants[group.variant_group_id]?.id === option.id;
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleVariantSelect(group.variant_group_id, option)}
                          className={`px-3 py-1 rounded-full text-sm border transition ${
                            isSelected
                              ? 'bg-[#3F1907] text-[#F9EFD7] border-[#3F1907]'
                              : 'bg-white text-[#3F1907] border-[#58735A] hover:bg-[#58735A] hover:text-white'
                          }`}
                        >
                          {option.name}
                          {option.price_modifier !== 0 && (
                            <span className="ml-1 text-xs">
                              {option.price_modifier > 0 ? '+' : ''}${Number(option.price_modifier).toFixed(2)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}

            {Object.keys(selectedVariants).length > 0 && (
              <p className="text-right text-sm font-bold text-[#3F1907] mb-3">
                Precio final: ${calculateModalPrice().toFixed(2)}
              </p>
            )}

            <div className="flex gap-2 mt-2">
              <button
                onClick={() => {
                  setModalOpen(false);
                  setCurrentItem(null);
                  setSelectedVariants({});
                  setItemVariantGroups([]);
                }}
                className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-2 rounded-md text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleModalConfirm}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-md text-sm font-bold"
              >
                Agregar al carrito
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MenuPOS;